/**
 * Room session orchestration: create, join, silent re-join and leave.
 *
 * Seat tokens are stored per room code. Whenever the transport (re)connects,
 * the current room is re-joined with the stored token — page reloads, network
 * blips and server restarts all resume the same seat without user action.
 */
import type { GameSettings, JoinResult, RoomPeek } from '@wordloom/engine';
import { navigate, roomPath } from '../router';
import {
  KEYS,
  clearSeatToken,
  forgetRoom,
  getSeatToken,
  readString,
  rememberRoom,
  setSeatToken,
  writeString,
} from '../lib/storage';
import { useRoomStore } from '../state/roomStore';
import { useDraftStore } from '../state/draftStore';
import * as api from './api';
import { getBackend, setOnConnected } from './backend';
import { isTransportError, type ClientErrorPayload, type Result } from './errors';

/** The player's remembered display name ('' when never set). */
export function storedName(): string {
  return readString(KEYS.name) ?? '';
}

export function saveName(name: string): void {
  const trimmed = name.trim();
  if (trimmed) writeString(KEYS.name, trimmed);
}

function connect(): void {
  getBackend().connect();
}

let joinInFlight: { code: string; promise: Promise<Result<JoinResult>> } | null = null;

/**
 * Join (or re-join with a token) and record the outcome in the room store.
 * Concurrent calls for the same room share one request, so the "connected"
 * hook and an explicit join can never race each other into two seats.
 */
function join(code: string, name: string, token?: string): Promise<Result<JoinResult>> {
  if (joinInFlight?.code === code) return joinInFlight.promise;
  const store = useRoomStore.getState();
  store.setJoining(true);
  const promise = api.joinRoom(code, name, token).then((res) => {
    joinInFlight = null;
    const s = useRoomStore.getState();
    // The user navigated away (or into another room) while we were joining.
    if (s.session?.code !== code) {
      s.setJoining(false);
      return res;
    }
    if (res.ok) {
      setSeatToken(res.code, res.token);
      s.setJoined(res.code, res.seat);
      rememberRoom(res.code, null);
      return res;
    }
    handleJoinError(code, res.error, Boolean(token));
    return res;
  });
  joinInFlight = { code, promise };
  return promise;
}

/** Drop everything this tab holds about its room: view, session, draft, pending actionIds. */
function resetRoomState(): void {
  useRoomStore.getState().clearRoom();
  useDraftStore.getState().reset();
  api.intentIds.clear();
}

function handleJoinError(code: string, error: ClientErrorPayload, hadToken: boolean): void {
  const s = useRoomStore.getState();
  if (error.code === 'ROOM_NOT_FOUND') {
    // The room expired, or the server restarted and lost it: the stored seat is
    // worthless and a board still on screen is dead. Reset like leave() does,
    // THEN record the error (clearRoom wipes joinError), so RoomScreen falls
    // through to JoinForm's "We couldn't find that room" notice.
    forgetRoom(code);
    resetRoomState();
    useRoomStore.getState().setJoinError(error);
    return;
  }
  if (error.code === 'ROOM_FULL' && hadToken) {
    // Our token no longer matches a seat (room re-created with the same code).
    clearSeatToken(code);
  }
  if (hadToken && isTransportError(error)) {
    // A silent re-join that could not reach the server: the connection banner
    // already explains it, and the next (re)connect retries automatically.
    s.setJoining(false);
    return;
  }
  s.setJoinError(error);
}

/** Re-join the current room with its stored token (no-op without one). */
export async function rejoin(): Promise<void> {
  const session = useRoomStore.getState().session;
  if (!session || session.suspended) return;
  const token = getSeatToken(session.code);
  if (!token) return;
  await join(session.code, storedName() || 'Player', token);
}

/**
 * Called when the route becomes `/r/CODE`. With a stored token the seat is
 * reclaimed automatically; without one the join form is shown.
 */
export function enterRoom(code: string): void {
  const store = useRoomStore.getState();
  const current = store.session;
  if (current?.code === code && current.joined) return;
  store.startSession(code);
  if (getSeatToken(code)) {
    connect();
    void rejoin();
  } else {
    // Connect early so the join form submits instantly.
    connect();
  }
}

/**
 * Join form: who sent the invite and with which settings. A dead link (no such
 * room, or both seats taken) shows its notice straight away instead of after
 * the player has typed a name. Anything else (offline, timeout) returns null
 * and the form simply works without the extra line.
 */
export async function peekInvite(code: string): Promise<RoomPeek | null> {
  const res = await api.peekRoom(code);
  if (res.ok) return { hostName: res.hostName, settings: res.settings };
  const s = useRoomStore.getState();
  const stillHere = s.session?.code === code && !s.session.joined && !s.joining && s.view === null;
  if (stillHere && (res.error.code === 'ROOM_NOT_FOUND' || res.error.code === 'ROOM_FULL')) {
    handleJoinError(code, res.error, false);
  }
  return null;
}

/** Join form submit: take a free seat in `code` as `name`. */
export async function joinWithName(code: string, name: string): Promise<Result<JoinResult>> {
  saveName(name);
  const store = useRoomStore.getState();
  if (store.session?.code !== code) store.startSession(code);
  connect();
  return join(code, name.trim(), getSeatToken(code) ?? undefined);
}

/** Lobby "Create room": create, take seat 0 and navigate into the room. */
export async function createAndEnter(
  name: string,
  settings: Partial<GameSettings>,
): Promise<Result<JoinResult>> {
  saveName(name);
  connect();
  // No session exists until the ack names the room, so `joining` is what lets
  // a room:state that beats the ack through (see roomStore.receiveView).
  useRoomStore.getState().setJoining(true);
  const res = await api.createRoom(name.trim(), settings);
  if (!res.ok) {
    useRoomStore.getState().setJoining(false);
    return res;
  }
  setSeatToken(res.code, res.token);
  const store = useRoomStore.getState();
  store.startSession(res.code);
  store.setJoined(res.code, res.seat);
  rememberRoom(res.code, null);
  navigate(roomPath(res.code));
  return res;
}

/**
 * Leave the room and go back to the lobby. While waiting this frees the seat
 * (and forgets the room); mid-game it is only a disconnect — the stored token
 * keeps the seat resumable from the lobby's Resume list.
 */
export function leave(opts: { forget: boolean }): void {
  const store = useRoomStore.getState();
  const code = store.session?.code;
  // Whenever a session exists, not only when joined: mid-reconnect `joined` is
  // false, yet a re-join may already be in flight and seat this socket. The
  // server treats a leave from an unseated socket as a no-op, and a failed ack
  // (offline) needs no handling — clearing the session below is what stops the
  // next 'connect' from re-joining.
  if (store.session) void api.leaveRoom();
  if (code && opts.forget) forgetRoom(code);
  resetRoomState();
  navigate('/');
}

/**
 * Browser Back from a room to "/": leave like the Leave button would (mid-game
 * only a disconnect — the seat stays resumable). Checks the session, not
 * `joined`: during a reconnect `joined` is false, and skipping leave() then
 * left a ghost session that the next 'connect' silently re-joined from the
 * lobby (opponent saw us online, the lobby tab title said "● Your turn").
 */
export function leaveIfInRoom(): void {
  if (useRoomStore.getState().session) leave({ forget: false });
}

/** "Use this tab" after being replaced by another tab: take the seat back. */
export function reclaimSeat(): void {
  const store = useRoomStore.getState();
  const code = store.session?.code;
  if (!code) return;
  store.setKicked(null);
  store.startSession(code);
  connect();
  void rejoin();
}

/** A game intent was rejected because this socket lost its seat: re-join. */
export function recoverSession(error: ClientErrorPayload): void {
  if (error.code !== 'NOT_IN_ROOM') return;
  useRoomStore.getState().markDisconnected();
  void rejoin();
}

/** Install once at startup: every (re)connection re-joins the current room. */
export function installSession(): void {
  setOnConnected(() => {
    void rejoin();
  });
}
